import { Building2, MapPin } from "lucide-react";
import { data, Link } from "react-router";
import { DirectoryPagination } from "~/components/directory/directory-pagination";
import { BreadcrumbJsonLd } from "~/components/seo/breadcrumb-jsonld";
import { companySlug } from "~/lib/company-profile";
import { envConfig } from "~/lib/env-validation";
import { generateMetaTags, SITE_URL } from "~/lib/seo";
import type { Route } from "./+types/odvetvi-detail";

// --- Types ---

interface CompanyListItem {
	ico: string;
	name: string;
	city: string | null;
	status: string;
}

interface NaceCompaniesResponse {
	companies: CompanyListItem[];
	total: number;
	page: number;
	limit: number;
	naceCode: string;
	naceDescription: string;
	sectionLabel: string;
}

const PAGE_LIMIT = 20;

// --- Status labels ---

const STATUS_LABELS: Record<string, { label: string; className: string }> = {
	ACTIVE: { label: "Aktivní", className: "badge badge-soft badge-success" },
	DISSOLVED: { label: "Zrušená", className: "badge badge-soft badge-error" },
	IN_LIQUIDATION: { label: "V likvidaci", className: "badge badge-soft badge-warning" },
	IN_INSOLVENCY: { label: "V insolvenci", className: "badge badge-soft badge-error" },
	SUSPENDED: { label: "Pozastavená", className: "badge badge-soft badge-warning" },
};

// --- Loader ---

export async function loader({ params, request }: Route.LoaderArgs) {
	const { naceSlug } = params;
	const requestedPage = Number(new URL(request.url).searchParams.get("page") ?? "1");
	const page = Number.isSafeInteger(requestedPage) && requestedPage > 0 ? requestedPage : 1;
	const url = new URL(`/directory/nace/${encodeURIComponent(naceSlug)}`, envConfig.apiBaseUrl);
	url.searchParams.set("page", String(page));
	url.searchParams.set("limit", String(PAGE_LIMIT));
	const res = await fetch(url);

	if (res.status === 404) {
		throw data(null, { status: 404 });
	}
	if (!res.ok) {
		throw data(null, { status: 500 });
	}

	const result: NaceCompaniesResponse = await res.json();
	const totalPages = Math.max(1, Math.ceil(result.total / PAGE_LIMIT));
	if (page > totalPages) {
		throw data(null, { status: 404 });
	}

	return { initial: result, naceSlug, page };
}

// --- Meta ---

export function meta({ data: loaderData }: Route.MetaArgs) {
	if (!loaderData) {
		return [
			{ title: "Odvětví nenalezeno | FirmIndex" },
			{ name: "robots", content: "noindex, nofollow" },
		];
	}

	const { initial, naceSlug, page } = loaderData;
	const pageSuffix = page > 1 ? ` – strana ${page}` : "";
	const title = `${initial.sectionLabel} – firmy v oboru${pageSuffix} | FirmIndex`;
	const description = `Seznam firem v odvětví ${initial.sectionLabel}. Celkem ${initial.total.toLocaleString("cs-CZ")} firem v tomto oboru v České republice.${page > 1 ? ` Strana ${page}.` : ""}`;
	const path = `/directory/odvetvi/${naceSlug}`;

	return generateMetaTags({
		title,
		description,
		canonical: `${SITE_URL}${path}${page > 1 ? `?page=${page}` : ""}`,
	});
}

// --- Components ---

function CompanyRow({ company }: { company: CompanyListItem }) {
	const status = STATUS_LABELS[company.status] ?? {
		label: company.status,
		className: "badge badge-soft",
	};

	return (
		<Link
			to={`/firma/${company.ico}-${companySlug(company.name)}`}
			className="card-surface flex items-center justify-between gap-3 rounded-2xl p-4 transition-all hover:-translate-y-0.5 hover:shadow-md"
		>
			<div className="flex flex-col gap-1 min-w-0">
				<h3 className="text-sm font-semibold truncate">{company.name}</h3>
				<div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
					<span className="font-mono">IČO: {company.ico}</span>
					{company.city && (
						<span className="flex items-center gap-1">
							<MapPin className="h-3 w-3" />
							{company.city}
						</span>
					)}
				</div>
			</div>
			<span className={status.className}>{status.label}</span>
		</Link>
	);
}

// --- Page ---

export default function OdvetviDetailPage({ loaderData }: Route.ComponentProps) {
	const { initial, naceSlug, page } = loaderData;
	const totalPages = Math.ceil(initial.total / PAGE_LIMIT);

	return (
		<>
			<BreadcrumbJsonLd
				items={[
					{ name: "FirmIndex", url: "/" },
					{ name: "Odvětví", url: "/directory/odvetvi" },
					{ name: initial.sectionLabel, url: `/directory/odvetvi/${naceSlug}` },
				]}
			/>

			<div>
				{/* Header */}
				<div className="px-4 pt-6">
					<div className="public-hero mx-auto max-w-4xl rounded-3xl px-6 py-8 sm:px-8">
						<nav className="mb-3 text-sm text-muted-foreground">
							<Link to="/directory/odvetvi" className="hover:text-primary transition-colors">
								Odvětví
							</Link>
							<span className="mx-2">/</span>
							<span>{initial.sectionLabel}</span>
						</nav>
						<h1 className="text-2xl font-bold sm:text-3xl">{initial.sectionLabel}</h1>
						<p className="mt-2 text-muted-foreground">
							{initial.naceDescription !== initial.sectionLabel && <>{initial.naceDescription}. </>}
							Celkem <strong>{initial.total.toLocaleString("cs-CZ")}</strong>{" "}
							{initial.total === 1
								? "firma"
								: initial.total >= 2 && initial.total <= 4
									? "firmy"
									: "firem"}{" "}
							v tomto odvětví.
						</p>
					</div>
				</div>

				{/* Content */}
				<main className="mx-auto max-w-4xl px-4 py-6">
					<div className="flex flex-col gap-3">
						{/* Results */}
						{initial.companies.length > 0 &&
							initial.companies.map((company) => (
								<CompanyRow key={company.ico} company={company} />
							))}

						{/* Empty state */}
						{initial.companies.length === 0 && (
							<div className="flex flex-col items-center gap-2 py-16 text-center">
								<Building2 className="h-12 w-12 text-muted-foreground" />
								<p className="text-sm font-medium">V tomto odvětví zatím nejsou žádné firmy</p>
							</div>
						)}

						{/* Pagination */}
						<DirectoryPagination
							basePath={`/directory/odvetvi/${naceSlug}`}
							page={page}
							totalPages={totalPages}
						/>
					</div>
				</main>
			</div>
		</>
	);
}
